/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**
 * @name MenuItemRenderer
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Componente recursivo para renderização dos itens de menu
 * @package src/components/layouts/UserLayout/components/UserSideBar/components/MenuItemRenderer.tsx
 **/

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuItemProps } from '../types';
import useThemeContext from '../../../../../../themes/hooks/useThemeContext';

export const MenuItemRenderer: React.FC<MenuItemProps> = ({ item, onClose, level = 1, openKeys, onToggle }) => {
     const location = useLocation();
     const navigate = useNavigate();
     const { theme } = useThemeContext();

     const isActive = item.path ? location.pathname === item.path : item.pathIncludes ? location.pathname.includes(item.pathIncludes) : false;
     const hasChildren = item.children && item.children.length > 0;
     const isOpen = openKeys.includes(item.key);

     // Determina se este é um menu de nível pai (nível 1)
     const isParentLevel = level === 1;

     const handleClick = () => {
          if (hasChildren) {
               // Passa se é um menu pai quando chamar o toggle
               onToggle(item.key, isParentLevel);
          } else if (item.path) {
               navigate(item.path);
               onClose?.();
          }
     };

     // Calcular espaçamento progressivo baseado no nível
     const indentPadding = level === 1 ? 2 : 8 + (level - 1) * 8;

     return (
          <>
               <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                         onClick={handleClick}
                         sx={{
                              paddingLeft: `${indentPadding}px`,
                              backgroundColor: isActive ? theme.palette.divider : 'transparent',
                              color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                              // Adicionar indicador visual para níveis mais profundos
                              borderLeft: level > 1 ? `2px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}` : 'none',
                              marginLeft: level > 1 ? '4px' : 0,
                              transition: 'all 0.2s',
                              '&:hover': {
                                   backgroundColor: theme.palette.action.hover,
                              },
                         }}
                    >
                         <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                              sx={{ width: '100%' }}
                         >
                              {level > 1 && (
                                   <Box
                                        sx={{
                                             width: 4,
                                             height: 4,
                                             borderRadius: '50%',
                                             backgroundColor: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                             marginRight: 1,
                                             opacity: 0.7,
                                        }}
                                   />
                              )}
                              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{React.cloneElement(item.icon, { sx: { color: 'inherit', fontSize: level > 1 ? '1.2rem' : '1.4rem' } })}</ListItemIcon>
                              <ListItemText
                                   primary={item.text}
                                   primaryTypographyProps={{
                                        fontSize: level > 1 ? '0.9rem' : '1rem',
                                        fontWeight: isActive ? 'medium' : 'normal',
                                   }}
                              />
                              {hasChildren && (isOpen ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />)}
                         </Stack>
                    </ListItemButton>
               </ListItem>

               {hasChildren && (
                    <Collapse
                         in={isOpen}
                         timeout='auto'
                         unmountOnExit
                    >
                         <List
                              component='div'
                              disablePadding
                              sx={{
                                   // Adicionar linha vertical para indicar agrupamento
                                   borderLeft: level > 1 ? `1px dashed ${theme.palette.divider}` : 'none',
                                   marginLeft: level > 1 ? '12px' : 0,
                              }}
                         >
                              {item.children!.map((child) => (
                                   <MenuItemRenderer
                                        key={child.key}
                                        item={child}
                                        onClose={onClose}
                                        level={level + 1}
                                        openKeys={openKeys}
                                        onToggle={onToggle}
                                   />
                              ))}
                         </List>
                    </Collapse>
               )}
          </>
     );
};
